<template>
  <div id="cashlog-filter-box" @show="reset">
    <toggle-box
      :text="$t('FILTERS')"
      white
      pure-icon
      ref="box"
      @close="cancel"
    >
      <template v-slot:icon>
        <font-awesome-icon class="icon" :icon="faFilter" />
      </template>

      <template v-slot:content>
        <div class="filters">
          <bamboo-input
            :items="fundsList"
            :label="$t('FUND')"
            v-model="funds"
            required
            multiple
            ref="fundsInput"
          />

          <bamboo-input
            :items="typesList"
            :label="$t('CASH_LOG_TYPE')"
            v-model="types"
            required
            multiple
            ref="typesInput"
          />

          <bamboo-input
            :items="directionsList"
            :label="$t('CASH_IN_OUT')"
            v-model="directions"
            required
            multiple
            ref="directionsInput"
          />

          <div class="amounts">
            <text-input
              v-model="minAmount"
              ref="minAmountInput"
              :label="$t('FLOOR_AMOUNT')"
              dynamic-label
              :format="addCommas"
              :hint="changeDigitToViText(minAmount, false)"
            />

            <text-input
              v-model="maxAmount"
              ref="maxAmountInput"
              :label="$t('CEILING_AMOUNT')"
              dynamic-label
              :format="addCommas"
              :hint="changeDigitToViText(maxAmount, false)"
              :validate="checkAmountRange"
            />
          </div>

          <auto-complete-input
            :items="categoriesList"
            :label="$t('CASH_LOG_CATEGORY')"
            v-model="categories"
            dynamic-label
            required
            multiple
            ref="categoriesInput"
          />
        </div>

        <div class="btns">
          <primary-button :text="$t('CLEAR')" rounded-corner independence @click="clear" />
          <primary-button :text="$t('FILTER')" rounded-corner blue @click="apply" />
        </div>
      </template>
    </toggle-box>
  </div>
</template>

<script>
import { ref, watch, computed } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';

import { addCommas, removeCommas, changeDigitToViText } from '@/helpers';
import { CASH_LOG_TYPES } from '@/consts';

import ToggleBox from '@/components/ToggleBox';
import TextInput from '@/components/TextInput';
import BambooInput from '@/components/BambooInput';
import AutoCompleteInput from '@/components/AutoCompleteInput';
import PrimaryButton from '@/components/PrimaryButton';

const { useState: useAppState } = createNamespacedHelpers('app');
const { useState, useActions } = createNamespacedHelpers('cashLog');

export default {
  components: {
    FontAwesomeIcon,
    ToggleBox,
    TextInput,
    BambooInput,
    AutoCompleteInput,
    PrimaryButton
  },
  setup(props, { root }) {
    const { funds: funds_, cashLogCategories } = useAppState(['funds', 'cashLogCategories']);
    const { filters } = useState(['items', 'filters', 'total']);
    const { applyFilter } = useActions(['applyFilter']);

    const box = ref(null);
    const minAmount = ref(filters.minAmount);
    const maxAmount = ref(filters.maxAmount);
    const minAmountInput = ref(null);
    const maxAmountInput = ref(null);

    const fundsList = computed(() => funds_.value.map((b) => ({
      value: b.id,
      text: b.name
    })));

    const funds = ref(filters.funds || fundsList.value.map((item) => item.value));
    const fundsInput = ref(null);

    const typesList = Object.keys(CASH_LOG_TYPES).map((key) => ({
      value: key,
      text: root.$t(key)
    }));

    const types = ref(filters.types || typesList.map((item) => item.value));
    const typesInput = ref(null);

    const directionsList = [
      {
        text: root.$t('CASH_IN'),
        value: 1
      },
      {
        text: root.$t('CASH_OUT'),
        value: -1
      }
    ];

    const directions = ref(filters.directions || directionsList.map((item) => item.value));
    const directionsInput = ref(null);

    const categoriesList = computed(() => cashLogCategories.value.map((c) => ({
      value: c.id,
      text: `${(c.direction > 0) ? root.$t('CASH_IN') : root.$t('CASH_OUT')} | ${root.$t(c.key)}`
    })));

    const categories = ref(filters.categories || []);
    const categoriesInput = ref(null);

    const cancel = () => {
      box.value.toggle(false);
    };

    const checkAmountRange = () => {
      if (removeCommas(maxAmount.value) < removeCommas(minAmount.value)) {
        return 'CEILING_AMOUNT_IS_SMALLER_THAN_FLOOR_AMOUNT';
      }

      return '';
    };

    const reset = () => {
      minAmount.value = filters.minAmount;
      maxAmount.value = filters.maxAmount;
      funds.value = filters.funds || fundsList.value.map((item) => item.value);
      types.value = filters.types || typesList.map((item) => item.value);
      directions.value = filters.directions || directionsList.map((item) => item.value);
    };

    const apply = () => {
      const inputs = [
        fundsInput.value,
        typesInput.value,
        directionsInput.value,
        minAmountInput.value,
        maxAmountInput.value
      ];

      let valid = true;

      inputs.forEach((input) => {
        const inputValidity = input.validateInput();
        valid = valid && inputValidity;
      });

      if (valid) {
        applyFilter({
          page: 1,
          minAmount: removeCommas(minAmount.value),
          maxAmount: removeCommas(maxAmount.value),
          funds: funds.value,
          types: types.value,
          directions: directions.value,
          categories: (categories.value && categories.value.length) ? categories.value : null
        });

        box.value.toggle(false);
      }
    };

    const clear = (e) => {
      e.stopPropagation();

      minAmount.value = '';
      maxAmount.value = '';
      funds.value = fundsList.value.map((item) => item.value);
      types.value = typesList.map((item) => item.value);
      directions.value = directionsList.map((item) => item.value);
      categories.value = [];
    };

    watch(minAmount, () => {
      maxAmountInput.value.validateInput();
    });

    return {
      box,
      faFilter,
      cancel,
      addCommas,
      changeDigitToViText,
      minAmount,
      maxAmount,
      checkAmountRange,
      minAmountInput,
      maxAmountInput,
      fundsList,
      funds,
      fundsInput,
      clear,
      apply,
      reset,
      typesList,
      types,
      typesInput,
      directionsList,
      directions,
      directionsInput,
      categories,
      categoriesList,
      categoriesInput
    };
  }
};
</script>

<style lang="scss">
@import '~@/assets/scss/colors';

#cashlog-filter-box {
  .filters {
    width: 300px;

    .amounts {
      display: flex;
      align-items: top;
      justify-content: space-between;

      .text-input {
        width: calc(50% - 5px);

        input {
          width: 100%;
          border-radius: 4px;
        }
      }
    }

    .input {
      margin-bottom: 20px;
    }

    .autocomplete-input {
      .text-input {
        margin-bottom: 0;
      }

      input {
        width: 100%;
        border-radius: 4px;
      }

      .items-holder {
        border-radius: 4px;
      }
    }
  }

  .btns {
    text-align: right;
    margin-top: 15px;
    margin-bottom: 5px;

    .primary-button {
      margin-left: 10px;
      padding-left: 20px;
      padding-right: 20px;
    }
  }
}
</style>
